import {
  Alert,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { SafeDateTimePicker } from "components/SafeDateTimePicker";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useLabellingLocations } from "hooks/useLabellingLocations";
import { useTenantTranslation } from "hooks/formatters";
import { useCallback, useState } from "react";
import {
  LabelReservationSettingsCreate,
  LabelReservationSettingsRead,
  NoReservationBehaviour,
} from "src/store/api/generatedApi";

dayjs.extend(utc);
import {
  BaseStrategy,
  composePriority,
  decomposePriority,
} from "./labelCoveragePriorityUtils";

type FormData = LabelReservationSettingsCreate;

const toFormData = (
  row: LabelReservationSettingsRead | null,
  template: LabelReservationSettingsRead | null
): FormData => {
  const source = row ?? template;
  return {
    name: row?.name ?? "",
    label_coverage_priority: source?.label_coverage_priority ?? "breadth",
    recent_window_buffer_hours: source?.recent_window_buffer_hours ?? 0,
    reserve_from: source?.reserve_from ?? undefined,
    reserve_until: source?.reserve_until ?? undefined,
    max_duration_seconds: source?.max_duration_seconds ?? undefined,
    allowed_coverage_gap_seconds:
      source?.allowed_coverage_gap_seconds ?? undefined,
    required_angles: source?.required_angles ?? undefined,
    maximum_number_of_labellers:
      source?.maximum_number_of_labellers ?? undefined,
    target_number_of_labellers: source?.target_number_of_labellers ?? undefined,
    location: source?.location ?? undefined,
    ignore_coverage: source?.ignore_coverage ?? false,
    no_reservation_behaviour:
      source?.no_reservation_behaviour ?? "no_reservation",
    fallback_priority: row?.fallback_priority ?? null,
  };
};

export const SettingsProfileForm = ({
  open,
  onClose,
  onSubmit,
  existing,
  template,
  otherFallbackPriorities,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (form: FormData) => Promise<void>;
  existing: LabelReservationSettingsRead | null;
  template: LabelReservationSettingsRead | null;
  otherFallbackPriorities: ReadonlySet<number>;
}) => {
  const { t } = useTenantTranslation();
  const { locationNames } = useLabellingLocations();
  const [form, setForm] = useState<FormData>(() =>
    toFormData(existing, template)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const handleChange = useCallback(
    (field: keyof FormData, value: string | number | boolean | null) => {
      setForm((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    setIsSaving(true);
    setSaveFailed(false);
    try {
      await onSubmit(form);
      onClose();
    } catch {
      setSaveFailed(true);
    } finally {
      setIsSaving(false);
    }
  }, [form, onSubmit, onClose]);

  const fallbackPriorityCollides =
    form.fallback_priority != null &&
    otherFallbackPriorities.has(form.fallback_priority);

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {existing ? t("editSettings") : t("createSettings")}
      </DialogTitle>
      <DialogContent>
        <Stack
          component="fieldset"
          disabled={isSaving}
          spacing={2}
          sx={{ border: 0, m: 0, p: 0, pt: 1 }}
        >
          {saveFailed ? (
            <Alert severity="error">{t("errorSavingChanges")}</Alert>
          ) : null}
          <TextField
            label={t("settingsName")}
            value={form.name}
            onChange={(e) => {
              handleChange("name", e.target.value);
            }}
            required
            size="small"
          />
          <TextField
            label={t("location")}
            disabled={isSaving}
            value={form.location ?? ""}
            onChange={(e) => {
              handleChange("location", e.target.value || null);
            }}
            select
            size="small"
          >
            <MenuItem value="">{t("auto")}</MenuItem>
            {locationNames.map((loc) => (
              <MenuItem key={loc} value={loc}>
                {loc}
              </MenuItem>
            ))}
          </TextField>
          <Stack direction="row" spacing={2}>
            <SafeDateTimePicker
              label={t("reserveFrom")}
              value={form.reserve_from ? dayjs(form.reserve_from).utc() : null}
              onChange={(newValue) => {
                handleChange(
                  "reserve_from",
                  newValue?.isValid() ? newValue.toISOString() : null
                );
              }}
              format="YYYY-MM-DD HH:mm:ss"
              timezone="UTC"
              enableAccessibleFieldDOMStructure={false}
              slotProps={{ field: { clearable: true } }}
            />
            <SafeDateTimePicker
              label={t("reserveUntil")}
              value={
                form.reserve_until ? dayjs(form.reserve_until).utc() : null
              }
              onChange={(newValue) => {
                handleChange(
                  "reserve_until",
                  newValue?.isValid() ? newValue.toISOString() : null
                );
              }}
              format="YYYY-MM-DD HH:mm:ss"
              timezone="UTC"
              enableAccessibleFieldDOMStructure={false}
              slotProps={{ field: { clearable: true } }}
            />
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {t("labellingPriority")}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    decomposePriority(form.label_coverage_priority).labelRecent
                  }
                  onChange={(e) => {
                    const { baseStrategy } = decomposePriority(
                      form.label_coverage_priority
                    );
                    handleChange(
                      "label_coverage_priority",
                      composePriority(e.target.checked, baseStrategy)
                    );
                  }}
                />
              }
              label={t("labelRecentFirst")}
            />
            <ToggleButtonGroup
              color="primary"
              value={
                decomposePriority(form.label_coverage_priority).baseStrategy
              }
              exclusive
              onChange={(_, newValue: BaseStrategy | null) => {
                if (newValue) {
                  const { labelRecent } = decomposePriority(
                    form.label_coverage_priority
                  );
                  handleChange(
                    "label_coverage_priority",
                    composePriority(labelRecent, newValue)
                  );
                }
              }}
            >
              <ToggleButton value="breadth">
                {t("prioritiseUnlabelledFootage")}
              </ToggleButton>
              <ToggleButton value="depth">
                {t("prioritiseMultiplePasses")}
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>
          <TextField
            label={t("maxDuration")}
            type="number"
            value={form.max_duration_seconds ?? ""}
            onChange={(e) => {
              handleChange(
                "max_duration_seconds",
                e.target.value ? parseInt(e.target.value, 10) : null
              );
            }}
            size="small"
          />
          <TextField
            label={t("targetLabellers")}
            type="number"
            value={form.target_number_of_labellers ?? ""}
            onChange={(e) => {
              handleChange(
                "target_number_of_labellers",
                e.target.value ? parseInt(e.target.value, 10) : null
              );
            }}
            size="small"
          />
          <TextField
            label={t("maxLabellers")}
            type="number"
            value={form.maximum_number_of_labellers ?? ""}
            onChange={(e) => {
              handleChange(
                "maximum_number_of_labellers",
                e.target.value ? parseInt(e.target.value, 10) : null
              );
            }}
            size="small"
          />
          <TextField
            label={t("allowedCoverageGap")}
            type="number"
            value={form.allowed_coverage_gap_seconds ?? ""}
            onChange={(e) => {
              handleChange(
                "allowed_coverage_gap_seconds",
                e.target.value ? parseFloat(e.target.value) : null
              );
            }}
            size="small"
          />
          <TextField
            label={t("requiredAngles")}
            value={form.required_angles ?? ""}
            onChange={(e) => {
              handleChange("required_angles", e.target.value || null);
            }}
            size="small"
            placeholder='all or ["center","right"]'
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.ignore_coverage}
                onChange={(e) => {
                  handleChange("ignore_coverage", e.target.checked);
                }}
              />
            }
            label={t("ignoreCoverage")}
          />
          <Stack spacing={1}>
            <Typography variant="subtitle2">
              {t("noReservationBehaviour")}
            </Typography>
            <ToggleButtonGroup
              color="primary"
              value={form.no_reservation_behaviour}
              exclusive
              onChange={(_, newValue: NoReservationBehaviour | null) => {
                if (newValue) {
                  handleChange("no_reservation_behaviour", newValue);
                }
              }}
            >
              <ToggleButton value="no_reservation">
                {t("behaviourNoReservation")}
              </ToggleButton>
              <ToggleButton value="fallback">
                {t("behaviourFallback")}
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              {t("noReservationBehaviourHelp")}
            </Typography>
          </Stack>
          <TextField
            label={t("fallbackPriority")}
            type="number"
            value={form.fallback_priority ?? ""}
            onChange={(e) => {
              handleChange(
                "fallback_priority",
                e.target.value ? parseInt(e.target.value, 10) : null
              );
            }}
            error={fallbackPriorityCollides}
            helperText={
              fallbackPriorityCollides
                ? t("fallbackPriorityCollisionWarning")
                : t("fallbackPriorityHelp")
            }
            slotProps={{ htmlInput: { min: 0 } }}
            size="small"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          {t("cancel")}
        </Button>
        <Button
          onClick={() => {
            void handleSubmit();
          }}
          variant="contained"
          disabled={isSaving || !form.name || fallbackPriorityCollides}
        >
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
